import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
} from "lucide-react";
import UserProfileMenu from "./UserProfileMenu";

const DOCS_TREE = [
  { id: "solution-brief", label: "Arta Solution Brief", type: "pdf", loadBase64: () => import("../assets/docs/artaSolutionBrief.b64.js") },
  {
    id: "support-engineer",
    label: "Support Engineer",
    children: [
      { id: "copilot-mode", label: "Copilot Mode", type: "pdf", loadBase64: () => import("../assets/docs/artaCopilotUserManual.b64.js") },
      { id: "batch-mode", label: "Batch Mode", type: "pdf", loadBase64: () => import("../assets/docs/artaBatchModeUserManual.b64.js") },
      { id: "open-cases", label: "Open Cases", type: "pdf", loadBase64: () => import("../assets/docs/artaOpenCasesAnalysisUserManual.b64.js") },
    ],
  },
  { id: "enterprise-user", label: "Enterprise User", type: "pdf", loadBase64: () => import("../assets/docs/artaEnterpriseUserManual.b64.js") },
  { id: "admin", label: "Admin", type: "pdf", loadBase64: () => import("../assets/docs/artaAdminPortalUserManual.b64.js") },
];

const base64ToBlobUrl = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const flattenDocs = (nodes) =>
  nodes.flatMap((node) => (node.children ? flattenDocs(node.children) : [node]));

const ALL_DOCS = flattenDocs(DOCS_TREE);

const TreeNode = ({ node, depth, selectedId, expandedIds, onSelect, onToggle }) => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = node.id === selectedId;

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => onToggle(node.id)}
          style={{ paddingLeft: 12 + depth * 16 }}
          className="w-full flex items-center gap-1.5 py-2 pr-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
        >
          {isExpanded ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
          {isExpanded ? <FolderOpen size={16} className="text-gray-400 flex-shrink-0" /> : <Folder size={16} className="text-gray-400 flex-shrink-0" />}
          <span className="font-medium truncate">{node.label}</span>
        </button>
        {isExpanded && (
          <div>
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedId={selectedId}
                expandedIds={expandedIds}
                onSelect={onSelect}
                onToggle={onToggle}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      style={{ paddingLeft: 12 + depth * 16 + 18 }}
      className={`w-full flex items-center gap-1.5 py-2 pr-3 text-sm rounded-lg transition-colors ${
        isSelected ? "bg-cyan-50 text-cyan-700 font-medium" : "text-gray-600 hover:bg-gray-50"
      }`}
    >
      <FileText size={15} className={`flex-shrink-0 ${isSelected ? "text-cyan-600" : "text-gray-400"}`} />
      <span className="truncate">{node.label}</span>
    </button>
  );
};

const DocumentationPage = ({ onLogout }) => {
  const [selectedId, setSelectedId] = useState("solution-brief");
  const [expandedIds, setExpandedIds] = useState(() => new Set(["support-engineer"]));

  const toggleNode = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedDoc = ALL_DOCS.find((doc) => doc.id === selectedId) || null;

  const [pdfUrl, setPdfUrl] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPdfUrl(null);

    if (!selectedDoc || selectedDoc.type !== "pdf") return;

    let cancelled = false;
    selectedDoc.loadBase64().then((mod) => {
      if (cancelled) return;
      const url = base64ToBlobUrl(mod.default);
      blobUrlRef.current = url;
      setPdfUrl(url);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedDoc]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  return (
    <div className="flex min-h-screen h-screen w-full bg-[#f5f5f5]">
      {/* Doc tree panel */}
      <aside className="w-[280px] flex-shrink-0 bg-white border-r border-gray-200 h-screen overflow-y-auto">
        <div className="px-4 py-5 border-b border-gray-100 flex items-center gap-2">
          <div className="w-7 h-7 flex-shrink-0 overflow-hidden">
            <img
              src="/A_bgless.png"
              alt="Arta"
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 70%" }}
            />
          </div>
          <h2 className="text-base font-semibold text-[#1f1f1f]">Documentation</h2>
        </div>
        <div className="py-2 px-2">
          {DOCS_TREE.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={setSelectedId}
              onToggle={toggleNode}
            />
          ))}
        </div>
      </aside>

      {/* Document viewer */}
      <div className="flex-1 relative h-screen flex flex-col overflow-hidden">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        {!selectedDoc ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <FileText size={36} className="text-gray-300 mb-3" />
            <p className="text-gray-500">Select a document from the left to view it.</p>
          </div>
        ) : selectedDoc.type === "pdf" ? (
          <>
            <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#1f1f1f] font-semibold text-lg">
                <FileText size={18} />
                {selectedDoc.label}
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              {pdfUrl ? (
                <iframe src={pdfUrl} title={selectedDoc.label} className="w-full h-full border-0" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                  Loading document...
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="w-12 h-12 rounded-xl bg-gray-200 text-gray-500 flex items-center justify-center mb-5">
              <FileText size={22} />
            </div>
            <h1 className="text-2xl font-semibold text-[#1f1f1f] mb-2">{selectedDoc.label}</h1>
            <p className="text-gray-500 text-base">Documentation coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentationPage;
